/**
 * Telegram Mini App integration.
 *
 * Goal: make the game own the whole screen and own every touch — so swipes
 * control the game instead of dragging / collapsing the mini-app window.
 *
 * Everything here is feature-detected and version-gated, so the module is a
 * no-op when running outside Telegram (desktop browser, CF preview, etc.).
 */

interface TelegramWebApp {
  ready(): void;
  expand(): void;
  isExpanded?: boolean;
  version?: string;
  platform?: string;
  isVersionAtLeast?(version: string): boolean;
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  isFullscreen?: boolean;
  lockOrientation?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  onEvent?(event: string, handler: (...args: unknown[]) => void): void;
}

function getWebApp(): TelegramWebApp | null {
  const tg = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp;
  // Outside Telegram the script still injects a stub; `platform === 'unknown'`
  // marks that case, where the gesture/fullscreen APIs would just throw.
  if (!tg || tg.platform === 'unknown') return null;
  return tg;
}

function atLeast(tg: TelegramWebApp, version: string): boolean {
  try {
    return tg.isVersionAtLeast ? tg.isVersionAtLeast(version) : false;
  } catch {
    return false;
  }
}

/**
 * Initialise the Telegram surface. Safe to call once at startup; each step is
 * guarded so an unsupported client simply skips it.
 */
export function initTelegram(): void {
  const tg = getWebApp();
  if (!tg) return;

  const safe = (label: string, fn: () => void): void => {
    try {
      fn();
    } catch (err) {
      console.warn(`[tg] ${label} failed`, err);
    }
  };

  // Signal the client we're ready and take the full viewport height.
  safe('ready', () => tg.ready());
  safe('expand', () => tg.expand());

  // Match the app chrome to the game background so there's no flash of theme.
  safe('headerColor', () => tg.setHeaderColor?.('#07060d'));
  safe('backgroundColor', () => tg.setBackgroundColor?.('#07060d'));

  // The important bit: stop the vertical swipe-to-close / window drag so the
  // joystick swipe stays inside the game (Bot API 7.7+).
  if (atLeast(tg, '7.7')) {
    safe('disableVerticalSwipes', () => tg.disableVerticalSwipes?.());
  }

  // True fullscreen (Bot API 8.0+): hides the header entirely.
  if (atLeast(tg, '8.0')) {
    safe('requestFullscreen', () => tg.requestFullscreen?.());
    safe('lockOrientation', () => tg.lockOrientation?.());
    // If fullscreen is refused, expand() above is still in effect as a fallback.
    safe('onFullscreenFailed', () =>
      tg.onEvent?.('fullscreenFailed', () => tg.expand()),
    );
  }
}
